import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import useOperationalStore from '../platform/store/useOperationalStore';
import { fmtDur } from '../platform/store/useOperationalStore';
import useShopSettings from '../platform/settings/useShopSettings';
import { moneyQueues, activeEstimate, estimateApprovalUrl } from '../platform/engine/estimateMoney';
import { buildInvoiceDraft } from '../platform/engine/invoiceDraft';
import { fetchCustomer } from '../platform/engine/identitySpine';
import {
  downloadQBInvoice, downloadQBInvoiceBatch, QB_FORMATS, DEFAULT_QB_FORMAT,
} from '../platform/export/qboExport';
import { buildApprovalSmsHref } from './ro/roMoney';
import { clean } from './ro/roHelpers';
import { nowSeconds } from '../platform/events/operationEvents';
import CollectionsView from './CollectionsView';
import CycleTimeReport from './CycleTimeReport';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import ErrorBoundary from '../components/ErrorBoundary';
import './MoneyView.css';

const client = generateClient();

// The Money workspace: the whole money loop in one place. Three queues —
// Unapproved (waiting on the customer), Unbilled (work done, no invoice),
// Unpaid (invoice out, balance open) — plus the cycle-time report and
// analytics. Absorbs the old /collections, /reports/cycle-time and
// /analytics destinations.

const TABS = [
  { id: 'unapproved', label: 'Unapproved' },
  { id: 'unbilled',   label: 'Unbilled' },
  { id: 'unpaid',     label: 'Unpaid' },
  { id: 'cycle-time', label: 'Reports' },
  { id: 'analytics',  label: 'Analytics' },
];

function fmtMoney(n) {
  return '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// One queue table: RO, customer, unit, amount, how long it's been stuck.
// Row click → the job page, where every action lives. `renderActions(row)`
// adds an actions cell whose buttons act in place without leaving Money.
function QueueTable({ rows, amountLabel, stallLabel, emptyText, now, renderActions }) {
  const navigate = useNavigate();
  if (!rows.length) return <div className="mv-empty">{emptyText}</div>;
  return (
    <table className="mv-table">
      <thead>
        <tr>
          <th>RO</th>
          <th>Customer</th>
          <th>Unit</th>
          <th className="mv-r">{amountLabel}</th>
          <th className="mv-r">{stallLabel}</th>
          {renderActions && <th className="mv-actions-col">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const { truck, money } = row;
          const unit = [truck.year, truck.make, truck.model].filter(Boolean).join(' ');
          const stallMs = money.stallMs ?? (truck.lastStatusChange
            ? now - (truck.lastStatusChange < 10_000_000_000 ? truck.lastStatusChange * 1000 : truck.lastStatusChange)
            : null);
          return (
            <tr key={truck.id} onClick={() => navigate(`/ro/${truck.id}`)} title="Open job">
              <td className="mv-ro">{truck.repairOrder}</td>
              <td className="mv-customer">{truck.companyName}</td>
              <td className="mv-unit">{unit || truck.truckNumber || '—'}</td>
              <td className="mv-r mv-amount">{money.total > 0 ? fmtMoney(money.remaining ?? money.total) : '—'}</td>
              <td className="mv-r mv-stall">{stallMs ? fmtDur(stallMs) : '—'}</td>
              {renderActions && (
                <td className="mv-actions" onClick={e => e.stopPropagation()}>
                  {renderActions(row)}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function MoneyView({ canViewReports }) {
  const trucks = useOperationalStore(s => s.trucks);
  const localUpdateTruck = useOperationalStore(s => s.localUpdateTruck);
  const settings = useShopSettings(s => s.settings);
  const location = useLocation();
  const navigate = useNavigate();
  const [now] = useState(Date.now());
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState('');
  // Which QuickBooks file the shop wants — defaults to whatever Settings →
  // Billing says they run (Online CSV vs Desktop IIF).
  const [qbFormat, setQbFormat] = useState(
    settings?.accounting?.quickbooksFormat || DEFAULT_QB_FORMAT);
  // Things QuickBooks won't accept verbatim (netted discounts, tax handling)
  // that the shop needs to see before importing.
  const [exportNotes, setExportNotes] = useState([]);

  function toast(msg) {
    setFlash(msg);
    setTimeout(() => setFlash(''), 2500);
  }

  // Persist an estimate change on a truck (same shape ROPage uses): upsert
  // the estimate in the array (replace by id, or prepend if new), write
  // optimistically, then mutate.
  async function saveEstimate(truck, nextEst) {
    const existing = clean(truck.estimates || []);
    const idx = existing.findIndex(e => e.id === nextEst.id);
    const estimates = idx >= 0
      ? existing.map((e, i) => i === idx ? nextEst : e)
      : [nextEst, ...existing];
    const patch = { estimates };
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
      return true;
    } catch (err) {
      console.error('[MoneyView] saveEstimate failed:', err);
      toast('Save failed — check connection');
      return false;
    }
  }

  // Download the QuickBooks import file for an already-priced estimate.
  function downloadQbo(truck, est) {
    return downloadQBInvoice(est, truck, settings, qbFormat);
  }

  // Every open invoice in one push to QuickBooks instead of one download per
  // RO. QuickBooks Online caps an import file at 100 invoices / 1,000 rows, so
  // this can come down as several files — say so rather than let the shop
  // import one and think they're done.
  function exportUnpaidBatch(rows) {
    const entries = rows
      .map(({ truck }) => ({ truck, estimate: activeEstimate(truck) }))
      .filter(e => e.estimate && ['invoiced', 'paid'].includes(e.estimate.status));
    if (!entries.length) { toast('No invoices to export yet'); return; }

    const { files, invoices, notes } = downloadQBInvoiceBatch(entries, settings, qbFormat);
    setExportNotes(notes);
    const fileNote = files.length > 1
      ? ` in ${files.length} files (QuickBooks caps each import at 100 invoices) — import all ${files.length}`
      : ` — ${files[0]}`;
    toast(`${invoices} invoice${invoices === 1 ? '' : 's'} exported${fileNote}`);
  }

  // One-click invoice for an unbilled job: price it via the shared
  // buildInvoiceDraft (same math as the estimate builder), confirm the
  // dollar figure, save it invoiced, then hand back the QuickBooks file —
  // the whole job -> invoice -> QBO chain without leaving Money.
  async function generateInvoice(truck) {
    // A job invoiced straight from this queue may never have had an estimate,
    // so the customer's Net terms have to be resolved here too — otherwise the
    // shop default silently overrides what this customer actually gets.
    const customer = truck.customerId ? await fetchCustomer(truck.customerId) : null;
    const termsDays = customer?.paymentTermsDays ?? null;
    if (customer?.poRequired && !truck.poNumber && !window.confirm(
      `${truck.companyName} requires a PO number on every invoice, and RO ${truck.repairOrder} has none.\n\nInvoice anyway?`
    )) return;
    const { draft, laborHours, hasEstimate, derivedLabor, total } = buildInvoiceDraft(truck, settings, { termsDays });
    if (total <= 0) {
      toast('Nothing to invoice — add labor or parts on the job first');
      return;
    }
    const laborNote = derivedLabor
      ? `\n(Labor estimated from ${laborHours} planned hrs — review on the job if unsure.)`
      : '';
    const src = hasEstimate ? 'the approved estimate' : 'this job\'s labor + parts';
    if (!window.confirm(
      `Invoice RO ${truck.repairOrder} for ${fmtMoney(total)} from ${src}?${laborNote}`
    )) return;

    setBusyId(truck.id);
    const ok = await saveEstimate(truck, clean(draft));
    setBusyId(null);
    if (!ok) return;
    const name = downloadQbo(truck, draft);
    toast(`Invoiced ${fmtMoney(total)} · ${name} downloaded`);
  }

  function resendApproval(truck, est) {
    const href = buildApprovalSmsHref(truck, est);
    if (!href) { toast('No approval link yet — open the job to send one'); return; }
    window.location.href = href;
  }

  async function copyApprovalLink(est) {
    if (!est?.token) { toast('No approval link yet — open the job to send one'); return; }
    const url = estimateApprovalUrl(est.token);
    try {
      await navigator.clipboard.writeText(url);
      toast('Approval link copied');
    } catch {
      window.prompt('Copy this approval link:', url);
    }
  }

  async function markDeclined(truck, est) {
    if (!window.confirm(`Mark the estimate for RO ${truck.repairOrder} as declined? It stays on the job — nothing is deleted.`)) return;
    setBusyId(truck.id);
    await saveEstimate(truck, clean({
      ...est,
      status: 'declined',
      declinedAt: nowSeconds(),
      declineReason: est.declineReason || 'Marked declined from Money queue',
      updatedAt: nowSeconds(),
    }));
    setBusyId(null);
    toast('Marked declined');
  }

  const urlTab = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState(TABS.some(t => t.id === urlTab) ? urlTab : 'unapproved');

  // Redirected old routes (/analytics, /reports/cycle-time) arrive after
  // mount — follow the URL when it names a tab.
  useEffect(() => {
    if (urlTab && urlTab !== tab && TABS.some(t => t.id === urlTab)) setTab(urlTab);
  }, [urlTab]);

  function go(id) {
    setTab(id);
    navigate(`/money?tab=${id}`, { replace: true });
  }

  const queues = useMemo(() => moneyQueues(trucks, now), [trucks, now]);
  const counts = {
    unapproved: queues.unapproved.length,
    unbilled:   queues.unbilled.length,
    unpaid:     queues.unpaid.length,
  };

  return (
    <div className="money-view">
      <div className="mv-tabs" role="tablist" aria-label="Money">
        {TABS.map(t => (
          <button key={t.id} role="tab" aria-selected={tab === t.id}
            className={`mv-tab${tab === t.id ? ' mv-tab--active' : ''}`}
            onClick={() => go(t.id)}>
            {t.label}
            {counts[t.id] > 0 && <span className="mv-tab-count">{counts[t.id]}</span>}
          </button>
        ))}
      </div>

      <div className="mv-body">
        {tab === 'unapproved' && (
          <QueueTable rows={queues.unapproved} now={now}
            amountLabel="Estimate" stallLabel="Waiting"
            emptyText="No estimates waiting on a customer. Money is moving."
            renderActions={({ truck }) => {
              const est = activeEstimate(truck);
              const declined = est?.status === 'declined';
              return (
                <div className="mv-btn-row">
                  <button className="mv-btn mv-btn--primary" disabled={busyId === truck.id}
                    onClick={() => resendApproval(truck, est)}>
                    {declined ? 'Re-send' : 'Resend text'}
                  </button>
                  <button className="mv-btn" disabled={busyId === truck.id}
                    onClick={() => copyApprovalLink(est)}>
                    Copy link
                  </button>
                  {!declined && (
                    <button className="mv-btn mv-btn--danger" disabled={busyId === truck.id}
                      onClick={() => markDeclined(truck, est)}>
                      Declined
                    </button>
                  )}
                </div>
              );
            }} />
        )}
        {tab === 'unbilled' && (
          <QueueTable rows={queues.unbilled} now={now}
            amountLabel="Value" stallLabel="Since done"
            emptyText="No finished work sitting uninvoiced."
            renderActions={({ truck }) => (
              <div className="mv-btn-row">
                <button className="mv-btn mv-btn--primary" disabled={busyId === truck.id}
                  onClick={() => generateInvoice(truck)}>
                  {busyId === truck.id ? 'Invoicing…' : 'Generate invoice'}
                </button>
              </div>
            )} />
        )}
        {tab === 'unpaid' && (
          <>
            <div className="mv-export-bar">
              <label htmlFor="mv-qb-format">QuickBooks file</label>
              <select id="mv-qb-format" className="mv-select"
                value={qbFormat} onChange={e => setQbFormat(e.target.value)}>
                {Object.values(QB_FORMATS).map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
              <span className="mv-export-hint">{QB_FORMATS[qbFormat]?.hint}</span>
              <button className="mv-btn mv-btn--primary"
                onClick={() => exportUnpaidBatch(queues.unpaid)}>
                Export all open invoices
              </button>
            </div>
            {exportNotes.length > 0 && (
              <div className="mv-export-notes">
                <strong>Before you import:</strong>
                <ul>{exportNotes.map(n => <li key={n}>{n}</li>)}</ul>
                <button className="mv-btn" onClick={() => setExportNotes([])}>Dismiss</button>
              </div>
            )}
            <CollectionsView />
          </>
        )}
        {tab === 'cycle-time' && (
          canViewReports
            ? <ErrorBoundary><CycleTimeReport /></ErrorBoundary>
            : <div className="mv-empty">Reports require the Full plan.</div>
        )}
        {tab === 'analytics' && (
          canViewReports
            ? <ErrorBoundary><AnalyticsDashboard /></ErrorBoundary>
            : <div className="mv-empty">Analytics requires the Ops plan or higher.</div>
        )}
      </div>

      {flash && <div className="mv-flash">{flash}</div>}
    </div>
  );
}
